import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import { getAssetChassisType } from './utils/intuneCompliance';
import Icon from './Icon';
import './OrderEquipmentForm.css';

const IN_STOCK_STATE = 'In Stock';
const REUSE_IDLE_DAYS = 60;
const REUSABLE_EXCLUDED_CHASSIS = new Set(['monitor']);

const getAssetState = (asset) => (asset.asset_state || asset.assetState || '').trim();

const isInStockAsset = (asset) => getAssetState(asset) === IN_STOCK_STATE;

const qualifiesByReuseRules = (asset) => {
  const lastConnectedValue = asset.LastConnected;
  const warrantyEndValue = asset['warranty-end'];
  if (!lastConnectedValue || !warrantyEndValue) return false;

  const lastConnected = new Date(lastConnectedValue);
  const warrantyEnd = new Date(warrantyEndValue);
  if (Number.isNaN(lastConnected.getTime()) || Number.isNaN(warrantyEnd.getTime())) return false;

  const now = new Date();
  const idleCutoff = new Date(now.getTime() - REUSE_IDLE_DAYS * 24 * 60 * 60 * 1000);
  return lastConnected < idleCutoff && warrantyEnd > now;
};

const isReusableCandidate = (asset, includeInStock) => {
  if (includeInStock && isInStockAsset(asset)) return true;
  if (isExcludedReusableChassis(asset)) return false;
  return qualifiesByReuseRules(asset);
};

function isExcludedReusableChassis(asset) {
  const chassisType = getAssetChassisType(asset);
  if (chassisType == null || String(chassisType).trim() === '') return false;
  return REUSABLE_EXCLUDED_CHASSIS.has(String(chassisType).trim().toLowerCase());
}

const getInitialFormData = () => ({
  vendor: '',
  model: '',
  type: '',
  profile: '',
  preAssignTo: '',
  quantity: 1,
  alternateDeliveryAddress: false,
  deliveryCompany: '',
  deliveryAtt: '',
  deliveryAddress: '',
  deliveryPostalCode: '',
  deliveryCity: '',
  deliveryCvrVat: ''
});

const OrderEquipmentForm = ({ onBack }) => {
  const { selectedTenant, authenticatedFetch, availableTenants } = useAuth();
  const [formData, setFormData] = useState(getInitialFormData);

  const [allAssets, setAllAssets] = useState([]);
  const [showInStockCandidates, setShowInStockCandidates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  const [approvedProfiles, setApprovedProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const activeTenantName = useMemo(() => {
    if (selectedTenant?.name) {
      return selectedTenant.name;
    }
    if (Array.isArray(availableTenants) && availableTenants.length === 1) {
      return availableTenants[0].name;
    }
    return '';
  }, [selectedTenant, availableTenants]);

  const profileOptions = useMemo(() => {
    const names = approvedProfiles
      .map((profile) => profile.profile)
      .filter((name) => typeof name === 'string' && name.trim());
    const unique = Array.from(new Set(names.map((name) => name.trim())));
    if (!unique.includes('Other')) {
      unique.push('Other');
    }
    return unique;
  }, [approvedProfiles]);

  const reusableCandidates = useMemo(
    () => allAssets.filter((asset) => isReusableCandidate(asset, showInStockCandidates)),
    [allAssets, showInStockCandidates]
  );

  const selectedProfileDescription = useMemo(() => {
    if (!formData.profile || formData.profile === 'Other') {
      return '';
    }
    const matchedProfile = approvedProfiles.find((profile) => profile.profile === formData.profile);
    return typeof matchedProfile?.description === 'string' ? matchedProfile.description.trim() : '';
  }, [formData.profile, approvedProfiles]);

  const fetchApprovedProfiles = useCallback(async (tenantName = activeTenantName) => {
    if (!tenantName) {
      setApprovedProfiles([]);
      return;
    }

    try {
      setProfilesLoading(true);
      setProfilesError(null);

      const params = new URLSearchParams({
        tenant: tenantName
      });
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/approved-hardware?${params.toString()}`);
      const data = await response.json();

      if (response.ok) {
        const profiles = Array.isArray(data.profiles) ? data.profiles : [];
        setApprovedProfiles(profiles);
      } else {
        setProfilesError(data.error || 'Unable to load approved profiles');
        setApprovedProfiles([]);
      }
    } catch (profileError) {
      console.error('Error fetching approved hardware profiles:', profileError);
      setProfilesError('Unable to load approved profiles');
      setApprovedProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  const fetchTenantUsers = useCallback(async (tenantName = activeTenantName) => {
    if (!tenantName) {
      setTenantUsers([]);
      return;
    }
    try {
      setUsersLoading(true);
      const params = new URLSearchParams({ tenant: tenantName, limit: '500' });
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tenant-users?${params.toString()}`);
      const data = await response.json();
      if (response.ok && data.success && Array.isArray(data.users)) {
        setTenantUsers(data.users);
      } else {
        setTenantUsers([]);
      }
    } catch {
      setTenantUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  const fetchAssets = useCallback(async (tenantName = activeTenantName) => {
    try {
      setLoading(true);
      setError(null);

      if (!tenantName) {
        setError('No tenant available');
        setAllAssets([]);
        return;
      }

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(tenantName)}`
      );
      const data = await response.json();

      if (response.ok) {
        setAllAssets(Array.isArray(data.assets) ? data.assets : []);
      } else {
        setAllAssets([]);
        setError(data.error || `Failed to fetch assets (Status: ${response.status})`);
      }
    } catch (fetchError) {
      console.error('Error fetching assets:', fetchError);
      setAllAssets([]);
      setError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [activeTenantName, authenticatedFetch]);

  useEffect(() => {
    if (activeTenantName) {
      fetchAssets(activeTenantName);
      fetchApprovedProfiles(activeTenantName);
      fetchTenantUsers(activeTenantName);
    } else {
      setAllAssets([]);
      setApprovedProfiles([]);
      setTenantUsers([]);
    }
  }, [activeTenantName, fetchAssets, fetchApprovedProfiles, fetchTenantUsers]);

  const handleProfileSelection = (value) => {
    const matchedProfile = approvedProfiles.find((profile) => profile.profile === value);
    if (matchedProfile) {
      setFormData(prev => ({
        ...prev,
        profile: value,
        vendor: matchedProfile.vendor || '',
        model: matchedProfile.model || '',
        type: matchedProfile.type || ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        profile: value
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'alternateDeliveryAddress') {
      setFormData((prev) =>
        checked
          ? { ...prev, alternateDeliveryAddress: true }
          : {
              ...prev,
              alternateDeliveryAddress: false,
              deliveryCompany: '',
              deliveryAtt: '',
              deliveryAddress: '',
              deliveryPostalCode: '',
              deliveryCity: '',
              deliveryCvrVat: ''
            }
      );
      return;
    }
    if (name === 'quantity') {
      const n = parseInt(value, 10);
      setFormData((prev) => ({
        ...prev,
        quantity: Number.isFinite(n) && n >= 1 ? Math.min(n, 999) : 1
      }));
      return;
    }
    if (name === 'profile') {
      handleProfileSelection(value);
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAssetClick = (asset) => {
    setFormData(prev => ({
      ...prev,
      vendor: asset.manufacturer || '',
      model: asset.model || '',
      type: asset.serial || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (!activeTenantName) {
      setSubmitMessage('Error: No tenant available');
      setIsSubmitting(false);
      return;
    }

    if (formData.alternateDeliveryAddress) {
      const missing = [];
      if (!String(formData.deliveryCompany || '').trim()) missing.push('Company');
      if (!String(formData.deliveryAddress || '').trim()) missing.push('Address');
      if (!String(formData.deliveryPostalCode || '').trim()) missing.push('Post code');
      if (!String(formData.deliveryCity || '').trim()) missing.push('City');
      if (missing.length) {
        setSubmitMessage(`❌ Please fill in the delivery form: ${missing.join(', ')}`);
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const response = await authenticatedFetch('/api/create-equipment-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenant: activeTenantName,
          formData: formData
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('✅ Successful - Your equipment order has been processed');
        setTicketUrl(data.ticketUrl);
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData(getInitialFormData());
          setSubmitMessage('');
          setTicketUrl('');
        }, 5000);
      } else {
        const errorMessage = data.error || 'Failed to process your request';
        console.error('Server error:', errorMessage, data);
        setSubmitMessage(`❌ ${errorMessage}`);
        setTicketUrl('');
      }
    } catch (error) {
      console.error('Error creating equipment order:', error);
      setSubmitMessage('❌ Failed to process your request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };


  return (
    <div className="order-equipment-container">
      <div className="form-header">
        <button onClick={onBack} className="back-button">
          <Icon name="arrowUp" size="small" />
          Back to Self Service
        </button>
        <h2>Order New Equipment</h2>
        <p>Request new hardware, software licenses, or IT equipment for your team</p>
      </div>

      <div className="form-content">
        <form onSubmit={handleSubmit} className="equipment-form">
          <div className="form-section">
            <h3>Equipment Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="profile">Profile *</label>
                <select
                  id="profile"
                  name="profile"
                  value={formData.profile}
                  onChange={handleInputChange}
                  required
                  disabled={profilesLoading}
                >
                  <option value="">Select a profile</option>
                  {profileOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {profilesLoading && (
                  <div className="input-hint">Loading approved profiles…</div>
                )}
                {profilesError && (
                  <div className="input-error">{profilesError}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="vendor">Vendor *</label>
                <input
                  type="text"
                  id="vendor"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Dell, HP, Lenovo"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="model">Model *</label>
                <input
                  type="text"
                  id="model"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., OptiPlex 7090, ThinkPad X1"
                />
              </div>
              <div className="form-group">
                <label htmlFor="type">Type *</label>
                <input
                  type="text"
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Laptop, Desktop, Monitor, Printer"
                />
              </div>
            </div>
            <div className={`form-row${selectedProfileDescription ? ' form-row-with-description' : ''}`}>
              <div className="form-group">
                <label htmlFor="quantity">Equipment quantity *</label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min={1}
                  max={999}
                  step={1}
                  required
                />
              </div>
              {selectedProfileDescription && (
                <div className="form-group profile-description-group" aria-live="polite">
                  <span className="profile-description-label">About this model</span>
                  <p className="profile-description">{selectedProfileDescription}</p>
                </div>
              )}
            </div>
            <div className="form-group form-group-checkbox">
              <input
                type="checkbox"
                id="alternateDeliveryAddress"
                name="alternateDeliveryAddress"
                checked={formData.alternateDeliveryAddress}
                onChange={handleInputChange}
              />
              <label htmlFor="alternateDeliveryAddress">Change delivery address</label>
            </div>
            {formData.alternateDeliveryAddress && (
              <div className="shipping-section" role="region" aria-label="Delivery address">
                <h4 className="shipping-section-title">Delivery address</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryCompany">Company *</label>
                    <input
                      type="text"
                      id="deliveryCompany"
                      name="deliveryCompany"
                      value={formData.deliveryCompany}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="organization"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="deliveryAtt">ATT</label>
                    <input
                      type="text"
                      id="deliveryAtt"
                      name="deliveryAtt"
                      value={formData.deliveryAtt}
                      onChange={handleInputChange}
                      placeholder="Attention / contact"
                      autoComplete="name"
                    />
                  </div>
                </div>
                <div className="form-row form-row-full">
                  <div className="form-group">
                    <label htmlFor="deliveryAddress">Address *</label>
                    <input
                      type="text"
                      id="deliveryAddress"
                      name="deliveryAddress"
                      value={formData.deliveryAddress}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="street-address"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryPostalCode">Post code *</label>
                    <input
                      type="text"
                      id="deliveryPostalCode"
                      name="deliveryPostalCode"
                      value={formData.deliveryPostalCode}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="postal-code"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="deliveryCity">City *</label>
                    <input
                      type="text"
                      id="deliveryCity"
                      name="deliveryCity"
                      value={formData.deliveryCity}
                      onChange={handleInputChange}
                      required={formData.alternateDeliveryAddress}
                      autoComplete="address-level2"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="deliveryCvrVat">CVR / VAT</label>
                    <input
                      type="text"
                      id="deliveryCvrVat"
                      name="deliveryCvrVat"
                      value={formData.deliveryCvrVat}
                      onChange={handleInputChange}
                      placeholder="Company registration / VAT ID"
                    />
                  </div>
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="preAssignTo">Pre-assign to user</label>
                <select
                  id="preAssignTo"
                  name="preAssignTo"
                  value={formData.preAssignTo}
                  onChange={handleInputChange}
                  disabled={usersLoading}
                >
                  <option value="">— None —</option>
                  {tenantUsers.map((user, index) => {
                    const displayName = user.displayName || user.displayname || user.userPrincipalName || user.userprincipalname || '—';
                    const key = user.userPrincipalName || user.userprincipalname || `${displayName}-${index}`;
                    return (
                      <option key={key} value={displayName}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
                {usersLoading && (
                  <div className="input-hint">Loading users…</div>
                )}
                <small className="input-hint">Optional: Assign equipment to a user when delivered</small>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onBack} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? 'Submitting...' : 'Submit Order'}
            </button>
          </div>

          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Error') || submitMessage.startsWith('❌') ? 'error' : 'success'}`}>
              <Icon name={submitMessage.includes('Error') || submitMessage.startsWith('❌') ? 'exclamation' : 'checkmark'} size="small" />
              <div>
                <div>{submitMessage}</div>
                {ticketUrl && (
                  <div className="ticket-link">
                    <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="ticket-url">
                      <Icon name="external" size="small" />
                      View Ticket in Zendesk
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>
      </div>

      <div className="assets-section">
        <div className="assets-header">
          <div className="assets-header-text">
            <h3>Reusable asset candidates</h3>
            <p>
              Assets not connected in over {REUSE_IDLE_DAYS} days with valid warranty.
              {showInStockCandidates
                ? ' Also showing assets marked In Stock (warranty and last-connected rules do not apply).'
                : ''}{' '}
              Click a row to fill the form.
            </p>
            <label className="in-stock-candidates-toggle">
              <input
                type="checkbox"
                checked={showInStockCandidates}
                onChange={(e) => setShowInStockCandidates(e.target.checked)}
              />
              <span>Include In Stock assets</span>
            </label>
          </div>
          <button
            onClick={() => fetchAssets(activeTenantName)}
            className="refresh-button"
            disabled={loading || !activeTenantName}
          >
            <Icon name="refresh" size="small" />
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <div className="loading-message">
            <Icon name="refresh" size="medium" />
            <span>Loading assets...</span>
          </div>
        ) : error ? (
          <div className="error-message">
            <Icon name="exclamation" size="medium" />
            <span>{error}</span>
          </div>
        ) : reusableCandidates.length === 0 ? (
          <div className="no-assets-message">
            <span>
              No reusable candidates found.
              {showInStockCandidates
                ? ' Try clearing the filter or set assets to In Stock on the Assets page.'
                : ' Enable “Include In Stock assets” to list stock items, or check warranty and last-connected dates.'}
            </span>
          </div>
        ) : (
          <div className="assets-grid">
            <div className="assets-table-header">
              <div className="asset-cell">Serial Number</div>
              <div className="asset-cell">Vendor</div>
              <div className="asset-cell">Model</div>
              <div className="asset-cell">Asset State</div>
              <div className="asset-cell">Last Connected</div>
              <div className="asset-cell">Warranty End</div>
            </div>
            {reusableCandidates.map((asset, index) => {
              const inStockOnly = isInStockAsset(asset) && !qualifiesByReuseRules(asset);
              return (
                <div
                  key={asset.id || index}
                  className={`asset-row clickable-asset-row${inStockOnly ? ' in-stock-candidate-row' : ''}`}
                  onClick={() => handleAssetClick(asset)}
                  title={inStockOnly ? 'Included via In Stock (idle/warranty rules waived)' : undefined}
                >
                  <div className="asset-cell">{asset.serial || 'N/A'}</div>
                  <div className="asset-cell">{asset.manufacturer || 'N/A'}</div>
                  <div className="asset-cell">{asset.model || 'N/A'}</div>
                  <div className="asset-cell">{getAssetState(asset) || '—'}</div>
                  <div className="asset-cell">{formatDate(asset.LastConnected)}</div>
                  <div className="asset-cell">{formatDate(asset['warranty-end'])}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderEquipmentForm;
